import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Row,
    Col,
    Card,
    Button,
    Modal,
} from "react-bootstrap";
import axiosInstance from '../../services/AxiosInstance';
import './commonstyle.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import CommonTable from '../components/CommonTable/CommonTable';
import { usePageToolbar } from '../layouts/nav/PageToolbarContext';
import '../tasks/tasklist.css';
import { getCurrentUserRole, canEditDeleteClassification, isBranchAdmin } from '../../utils/roleAccess';
import { useDocumentAccess } from '../../hooks/useDocumentAccess';
import { useCanDo } from '../../hooks/useCanDo';

const DocumentCategoryList = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { hasAccess, loading: accessLoading } = useDocumentAccess();
    const canCreateDocuments = useCanDo('documents', 'create_edit');
    const canEditClassifications = useCanDo('doc_classifications', 'create_edit');
    const canDeleteClassifications = useCanDo('doc_classifications', 'delete');
    const [documentcategories, setDocumentCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
    const [filteredDocumentCategories, setfilteredDocumentCategories] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedDocumentCategory, setselectedDocumentCategory] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [userBranchId, setUserBranchId] = useState(null);
    const [userDepartmentId, setUserDepartmentId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [departmentIdsUnderBranch, setDepartmentIdsUnderBranch] = useState([]);
    const [deleteCheckData, setDeleteCheckData] = useState(null);
    const [showFirstDeleteModal, setShowFirstDeleteModal] = useState(false);
    const [showSecondDeleteModal, setShowSecondDeleteModal] = useState(false);
    const [checkLoading, setCheckLoading] = useState(false);

    const notifyTopRight = (data) => {
        toast.success(`✔️ ${data}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
        });
    };

    const notifyError = (data) => {
        toast.error(`❌ ${data}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    };

    useEffect(() => {
        fetchDocumentCategories();
        loadUserDetails();
    }, []);

    const loadUserDetails = async () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            const role = getCurrentUserRole();
            setUserRole(role);
            setUserId(userData?.id);

            if (userData?.id) {
                try {
                    const userDetailsRes = await axiosInstance.get(`/auth/details/${userData.id}`);
                    const userDetails = userDetailsRes.data;
                    const branchId = userDetails?.Branch?.id || userDetails?.BranchId;
                    const departmentId = userDetails?.Department?.id || userDetails?.DepartmentId;
                    setUserBranchId(branchId);
                    setUserDepartmentId(departmentId);

                    // If user is branch admin, fetch all departments under their branch
                    if (branchId && isBranchAdmin(role)) {
                        try {
                            const corporateId = userData?.corporateId || userData?.id;
                            const departmentsRes = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}&branch_id=${branchId}`);
                            const departmentsData = departmentsRes.data;
                            const departments = departmentsData.data || departmentsData || [];
                            const deptIds = departments.map(dept => dept.id).filter(Boolean);
                            setDepartmentIdsUnderBranch(deptIds);
                        } catch (err) {
                            console.error('Error fetching departments under branch:', err);
                        }
                    }
                } catch (err) {
                    console.error('Error fetching user details:', err);
                }
            }
        } catch (err) {
            console.error('Error loading user details:', err);
        }
    };

    useEffect(() => {
        // Filter document classification based on search term
        if (searchTerm.trim() === '') {
            setfilteredDocumentCategories(documentcategories);
        } else {
            const filtered = documentcategories.filter(documentCategory =>
                documentCategory.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setfilteredDocumentCategories(filtered);
        }
    }, [documentcategories, searchTerm]);

    // Sync search from navbar ?search=
    useEffect(() => {
        const nextSearch = searchParams.get('search') || '';
        if (nextSearch !== searchTerm) setSearchTerm(nextSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const fetchDocumentCategories = async () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            const corporateId = userData?.corporateId || userData?.id;

            const res = await axiosInstance.get(`/document-categories?corporate_id=${corporateId}`);
            const data = res.data;

            if (res) {
                setDocumentCategories(data.data || data || []);
            }
        } catch (error) {
            console.error("document classifications list fetch error:", error);
            notifyError('Failed to fetch document classifications');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCheck = async (classificationId) => {
        setCheckLoading(true);
        try {
            const res = await axiosInstance.get(`/document-categories/${classificationId}/check`);
            const data = res.data;

            if (res && data.success) {
                const statistics = data.data?.statistics || {};
                const totalClassifications = statistics.total_classifications || 0;
                const totalDocuments = statistics.total_documents || 0;

                // If there are documents or classifications, show first warning modal
                if (totalClassifications > 0 || totalDocuments > 0) {
                    setDeleteCheckData(data.data);
                    setShowFirstDeleteModal(true);
                } else {
                    // No documents or classifications, proceed directly to delete
                    await performDelete();
                }
            } else {
                // If check fails, proceed with delete anyway
                await performDelete();
            }
        } catch (error) {
            console.error('Delete check error:', error);
            // If check fails, proceed with delete anyway
            await performDelete();
        } finally {
            setCheckLoading(false);
        }
    };

    const handleFirstDeleteConfirm = () => {
        if (!deleteCheckData) return;

        const statistics = deleteCheckData.statistics || {};
        const otherUserDocuments = statistics.other_user_documents || 0;
        const privateClassificationsByOthers = statistics.private_classifications_by_others || 0;

        // Close first modal
        setShowFirstDeleteModal(false);

        // If there are other users' documents or private classifications, show second warning
        if (otherUserDocuments > 0 || privateClassificationsByOthers > 0) {
            setShowSecondDeleteModal(true);
        } else {
            // No other users' content, proceed with delete
            performDelete();
        }
    };

    const handleSecondDeleteConfirm = () => {
        setShowSecondDeleteModal(false);
        performDelete();
    };

    const performDelete = async () => {
        if (!selectedDocumentCategory) return;
        const userData = JSON.parse(localStorage.getItem('user'));
        setDeleteLoading(true);
        try {
            const res = await axiosInstance.delete(`/document-categories/${selectedDocumentCategory.id}`);

            if (res) {
                notifyTopRight('Document classification deleted successfully');
                fetchDocumentCategories(); // Refresh the list
                setShowDeleteModal(false);
                setShowFirstDeleteModal(false);
                setShowSecondDeleteModal(false);
                setselectedDocumentCategory(null);
                setDeleteCheckData(null);
            }
        } catch (error) {
            console.error('Delete document classification error:', error);
            if (error.status === 409) {
                notifyError('Cannot delete document classification with staff');
            } else {
                notifyError(error.response?.data?.error || 'Failed to delete document classification');
            }
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDelete = async () => {
        // This function is kept for backward compatibility but now calls performDelete
        await performDelete();
    };

    const handleEdit = (documentCategory) => {
        navigate(`/documents/editDocumentClassification/${documentCategory.id}`, { state: { documentCategory } });
    };

    // Helper function to get created by name
    const getCreatedByName = (documentCategory) => {
        if (documentCategory.created_by_name) {
            return documentCategory.created_by_name;
        } else if (documentCategory.created_by) {
            if (typeof documentCategory.created_by === 'string') {
                return documentCategory.created_by;
            } else if (typeof documentCategory.created_by === 'object' && documentCategory.created_by.name) {
                return documentCategory.created_by.name;
            } else {
                return String(documentCategory.created_by);
            }
        } else if (documentCategory.creator_name) {
            return documentCategory.creator_name;
        }
        return 'N/A';
    };

    // Helper function to format shared branches and departments
    const getSharedInfo = (documentCategory) => {
        const sharedBranches = documentCategory.shared_with_branches || documentCategory.shared_branch_names || [];
        const sharedDepartments = documentCategory.shared_with_departments || documentCategory.shared_department_names || [];
        const sharedInfo = [];

        if (Array.isArray(sharedBranches) && sharedBranches.length > 0) {
            const branchNames = sharedBranches.map(b => {
                if (typeof b === 'string') return b;
                if (b && typeof b === 'object' && b.name) return b.name;
                return String(b);
            }).filter(Boolean);
            if (branchNames.length > 0) {
                sharedInfo.push(`Branches: ${branchNames.join(', ')}`);
            }
        }
        if (Array.isArray(sharedDepartments) && sharedDepartments.length > 0) {
            const deptNames = sharedDepartments.map(d => {
                if (typeof d === 'string') return d;
                if (d && typeof d === 'object' && d.name) return d.name;
                return String(d);
            }).filter(Boolean);
            if (deptNames.length > 0) {
                sharedInfo.push(`Departments: ${deptNames.join(', ')}`);
            }
        }

        return sharedInfo.length > 0 ? sharedInfo.join(' | ') : 'None (Only creator can see)';
    };

    // Define columns for CommonTable
    const columns = [
        {
            key: 'index',
            header: '#',
            accessor: (row, index) => index + 1,
        },
        {
            key: 'name',
            header: 'Classification Name',
            render: (row) => (
                <>
                    {row.is_favorite && (
                        <span className="me-2" style={{ color: '#ffc107' }}>★</span>
                    )}
                    <button
                        type="button"
                        className="btn btn-link p-0 align-baseline"
                        style={{
                            color: '#C42126',
                            textDecoration: 'underline',
                            fontWeight: 600,
                            fontSize: 'inherit',
                        }}
                        title="Open documents in this classification"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/documents/folder/${row.id}`);
                        }}
                    >
                        {row.name}
                    </button>
                </>
            ),
        },
        {
            key: 'parent_name',
            header: 'Parent Classification',
            render: (row) => (
                row.parent_id ? (
                    <button
                        type="button"
                        className="btn btn-link p-0 align-baseline text-muted"
                        style={{ textDecoration: 'underline', fontSize: 'inherit' }}
                        title="Open parent classification listing"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/documents/folder/${row.parent_id}`);
                        }}
                    >
                        {row.parent_name || '—'}
                    </button>
                ) : (
                    <span className="text-muted">{row.parent_name || '—'}</span>
                )
            ),
        },
        {
            key: 'createdBy',
            header: 'Created by',
            render: (row) => (
                <span className="text-muted">
                    {getCreatedByName(row)}
                </span>
            ),
        },
        {
            key: 'shared',
            header: 'Shared with Branch/Department',
            render: (row) => (
                <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                    {getSharedInfo(row)}
                </span>
            ),
        },
    ];

    // Define actions for CommonTable
    const getActions = (row, rowIndex) => {
        // Only show actions if corporate_id exists
        if (!row.corporate_id) return [];

        // Check if user can edit/delete this classification
        const canEditDelete = canEditDeleteClassification(
            row,
            userRole,
            userBranchId,
            userDepartmentId,
            userId,
            departmentIdsUnderBranch
        );

        const actions = [];

        actions.push({
            label: 'View Documents',
            icon: 'fas fa-folder-open',
            onClick: () => {
                navigate(`/documents/folder/${row.id}`);
            },
        });

        if (canCreateDocuments) {
            actions.push({
                label: 'Upload Document',
                icon: 'fas fa-upload',
                onClick: () => {
                    navigate(`/documents/upload?categoryId=${row.id}`);
                },
            });
        }

        const createdById = row.created_by_id || row.created_by?.id || row.creator_id;
        const isCreator = userId != null && createdById != null && String(createdById) === String(userId);

        if ((isCreator || canEditDelete) && canEditClassifications) {
            actions.push({
                label: 'Edit',
                icon: 'fas fa-edit',
                onClick: () => handleEdit(row),
            });
        }
        if (canEditDelete && canDeleteClassifications) {
            actions.push({
                label: 'Delete',
                icon: 'fas fa-trash',
                className: 'text-danger',
                onClick: async () => {
                    setselectedDocumentCategory(row);
                    await handleDeleteCheck(row.id);
                },
            });
        }

        return actions;
    };

    usePageToolbar(null, []);

    // Show loading while checking access
    if (accessLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Don't render if no access (will redirect)
    if (!hasAccess) {
        return null;
    }

    return (
        <Row style={{ marginBottom: "0px !important" }}>
            <ToastContainer />
            <Col lg={12}>
                <Card className="user-list-card">
                    <Card.Header style={{ display: 'block' }}>
                        <div className="d-flex justify-content-between align-items-center flex-wrap ">
                            <h5 className="mb-0" style={{ fontSize: '0.875rem', fontWeight: '600', color: '#2c3e50' }}>
                                Document Classifications
                            </h5>
                        </div>

                    </Card.Header>

                    <Card.Body style={{ minHeight: '400px', overflow: 'visible', padding: '1.5rem' }}>
                        {loading ? (
                            <div className="loading-container">
                                <div className="loading-spinner"></div>
                            </div>
                        ) : (
                            <div className="row">
                                <CommonTable
                                    columns={columns}
                                    data={filteredDocumentCategories}
                                    actions={getActions}
                                    emptyMessage={documentcategories.length === 0 ? 'No document Classifications found' : 'No document Classifications match your search criteria'}
                                    emptyStateIcon="fas fa-folder-open"
                                    getRowId={(row, index) => row.id || index}
                                    maxHeight="70vh"
                                    minHeight="400px"
                                />
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>

            {/* First Delete Confirmation Modal - Shows when there are documents/classifications */}
            <Modal show={showFirstDeleteModal} onHide={() => {
                setShowFirstDeleteModal(false);
                setDeleteCheckData(null);
                setselectedDocumentCategory(null);
            }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Warning: Classification Contains Content</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteCheckData && (() => {
                        const statistics = deleteCheckData.statistics || {};
                        const totalClassifications = statistics.total_classifications || 0;
                        const totalDocuments = statistics.total_documents || 0;
                        const currentUserDocuments = statistics.current_user_documents || 0;

                        return (
                            <div>
                                <p className="text-danger mb-0">
                                    <strong>Warning:</strong> The classification "<strong>{selectedDocumentCategory?.name}</strong>" contains sub-classifications and/or documents. Deleting this classification will also delete all sub-classifications and documents under it. This action cannot be undone.
                                </p>
                            </div>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <Button className="doc-form-cancel-btn" onClick={() => {
                        setShowFirstDeleteModal(false);
                        setDeleteCheckData(null);
                        setselectedDocumentCategory(null);
                    }}>
                        Cancel
                    </Button>
                    <Button
                        className="doc-form-submit-btn"
                        onClick={handleFirstDeleteConfirm}
                        disabled={checkLoading}
                    >
                        Yes, Delete Anyway
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Second Delete Confirmation Modal - Shows when there are other users' documents */}
            <Modal show={showSecondDeleteModal} onHide={() => {
                setShowSecondDeleteModal(false);
                setDeleteCheckData(null);
                setselectedDocumentCategory(null);
            }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Warning: Other Users' Content</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteCheckData && (() => {
                        const statistics = deleteCheckData.statistics || {};
                        const otherUserDocuments = statistics.other_user_documents || 0;
                        const privateClassificationsByOthers = statistics.private_classifications_by_others || 0;

                        return (
                            <div>
                                <p className="text-danger mb-0">
                                    <strong>Warning:</strong> This classification contains documents or sub-classifications uploaded/created by other users that you cannot see. Deleting this classification will permanently delete these items that belong to other users. This action cannot be undone.
                                </p>
                            </div>
                        );
                    })()}
                </Modal.Body>
                <Modal.Footer>
                    <Button className="doc-form-cancel-btn" onClick={() => {
                        setShowSecondDeleteModal(false);
                        setDeleteCheckData(null);
                        setselectedDocumentCategory(null);
                    }}>
                        Cancel
                    </Button>
                    <Button
                        className="doc-form-submit-btn"
                        onClick={handleSecondDeleteConfirm}
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Deleting...
                            </>
                        ) : (
                            'Yes, Delete Anyway'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Legacy Delete Modal - Kept for backward compatibility */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete the document classification "{selectedDocumentCategory?.name}"? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button className="doc-form-cancel-btn" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        className="doc-form-submit-btn"
                        onClick={handleDelete}
                        disabled={deleteLoading}
                    >
                        {deleteLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Deleting...
                            </>
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <BackToTop />
            <InternalFooter />
        </Row>
    );
};

export default DocumentCategoryList;